import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useClientV2} from "../../../../utils/hooks";
import {GET_SUBSCRIPTION_SALES_INSIGHTS_RQ_KEY} from "../constants";
import type {
  SubscriptionSalesInsightsApiRawResponse,
  SubscriptionSalesInsightsData,
} from "./types";

export type {SubscriptionSalesInsightsData} from "./types";

function parseNumericCurrencyMap(raw: Record<string, unknown>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw)) {
    const code = typeof k === "string" ? k.trim() : k;
    if (!code) {
      continue;
    }
    if (typeof v === "number" && Number.isFinite(v)) {
      out[code] = v;
      continue;
    }
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) {
        out[code] = n;
      }
    }
  }
  return out;
}

export function normalizeSubscriptionSalesInsights(
  envelope: SubscriptionSalesInsightsApiRawResponse
): SubscriptionSalesInsightsData {
  const d = envelope.data ?? {};

  const totalSalesRaw = d.totalSales ?? d.total_sales;
  let totalSales = 0;
  if (typeof totalSalesRaw === "number" && Number.isFinite(totalSalesRaw)) {
    totalSales = totalSalesRaw;
  } else if (typeof totalSalesRaw === "string" && totalSalesRaw.trim() !== "") {
    const n = Number(totalSalesRaw);
    totalSales = Number.isFinite(n) ? n : 0;
  }

  const mapRaw = d.totalRevenueByCurrency ?? d.total_revenue_by_currency ?? {};
  const totalRevenueByCurrency =
    mapRaw !== null &&
    typeof mapRaw === "object" &&
    !Array.isArray(mapRaw) &&
    Object.keys(mapRaw).length > 0
      ? parseNumericCurrencyMap(mapRaw as Record<string, unknown>)
      : {};

  return {totalSales, totalRevenueByCurrency};
}

/** `GET analytics/subscriptions/sales-insights/:subscriptionId` */
export function useGetSubscriptionSalesInsightsApi({
  subscriptionId,
  enabled,
}: {
  subscriptionId?: number;
  enabled?: boolean;
}) {
  const client = useClientV2();

  return useQuery<
    SubscriptionSalesInsightsData,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_SUBSCRIPTION_SALES_INSIGHTS_RQ_KEY, String(subscriptionId)],
    queryFn: async () => {
      if (!subscriptionId) {
        return {totalSales: 0, totalRevenueByCurrency: {}};
      }

      const body = await client(
        `analytics/subscriptions/sales-insights/${subscriptionId}`
      );

      return normalizeSubscriptionSalesInsights(
        body as SubscriptionSalesInsightsApiRawResponse
      );
    },
    enabled: !!subscriptionId && (enabled ?? true),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}
